import DOMPurify from "dompurify";

const ALLOWED_URL_PROTOCOLS = ["https:", "http:"];

/**
 * Sanitizes HTML content, allowing safe tags while stripping malicious scripts.
 * Use for content rendered via dangerouslySetInnerHTML.
 * @param {string} dirtyHtml
 * @returns {string}
 */
const sanitizeHtml = (dirtyHtml) => {
  if (!dirtyHtml) return "";
  return DOMPurify.sanitize(String(dirtyHtml), {
    USE_PROFILES: { html: true },
  });
};

/**
 * Strips all HTML tags, returning plain text only.
 * Use for content that should never contain markup.
 * @param {string} dirtyText
 * @returns {string}
 */
const sanitizeText = (dirtyText) => {
  if (!dirtyText) return "";
  return DOMPurify.sanitize(String(dirtyText), {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};

/**
 * Validates and sanitizes a URL, blocking javascript:, data: and other
 * dangerous protocols. Returns an empty string if the URL is unsafe.
 * Use for dynamic href attributes and window.open calls.
 * @param {string} url
 * @returns {string}
 */
const sanitizeUrl = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(String(url));
    if (!ALLOWED_URL_PROTOCOLS.includes(parsed.protocol)) return "";
    return url;
  } catch {
    return "";
  }
};

/**
 * Sanitizes a generic user text input, stripping all HTML tags.
 * Use for URL params, localStorage values, and form inputs from the user.
 * @param {string|number} value
 * @returns {string}
 */
const sanitizeInput = (value) => {
  if (value === null || value === undefined) return "";
  return DOMPurify.sanitize(String(value), {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};

const SANITIZE_TYPES = {
  html: sanitizeHtml,
  text: sanitizeText,
  url: sanitizeUrl,
  input: sanitizeInput,
};

/**
 * Reusable entry-point sanitizer. Routes to the correct sanitizer based on type.
 *
 * @param {string|number} value - The value to sanitize.
 * @param {"html"|"text"|"url"|"input"} [type="input"] - The sanitization strategy.
 *   - "html"  → allows safe HTML tags (for dangerouslySetInnerHTML)
 *   - "text"  → strips all HTML, returns plain text
 *   - "url"   → validates protocol, blocks javascript:/data: URLs
 *   - "input" → strips all HTML (default, for user inputs / URL params / localStorage)
 * @returns {string} The sanitized value.
 */
const sanitize = (value, type = "input") => {
  const sanitizer = SANITIZE_TYPES[type] ?? sanitizeInput;
  return sanitizer(value);
};

export { sanitize, sanitizeHtml, sanitizeText, sanitizeUrl, sanitizeInput };
export default sanitize;
